import { ChangeDetectionStrategy, Component, effect, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { getFeatureToggle } from '@capital-access/common/feature-toggle';
import { LocalizationService } from '@capital-access/common/localization';
import { LoadStatus } from '@capital-access/common/utils';
import { Alert, Breakpoint, ChipSize, ChipType, FireflyDrawerService } from '@capital-access/firefly/components';
import {
  createConfigurableItineraryReport,
  EXPORT_REPORT_ITINERARY_ACTIVITIES,
  EXPORT_REPORT_ITINERARY_EVENT,
  ITINERARY_MAX_ACTIVITY_SELECTION_LIMIT,
  ITINERARY_MAX_EXTERNAL_PARTICIPANT_LIMIT,
  ReportType
} from '@capital-access/reporting/common';
import { BaseReportDrawer } from '../../../components/report-drawer/base-report-drawer';
import { ReportSection, SectionSelectorListItem } from '../../../core/models/report-sections';
import { getSelectedSectionsFromGroups } from '../../../core/utils/section-settings.utils';
import { createItineraryCustomReport } from '../../+state/itinerary-report.actions';
import { SETTINGS_KEY } from '../../constants';
import { GroupedSectionSelectorListItem } from '../../models/itinerary-groupped-section-item';
import { CreateItineraryReportDto } from '../../models/itinerary-report.models';
import {
  DEFAULT_DISABLED_TOOLTIP,
  getSectionCustomDisabledTooltip,
  ItineraryReportLayout,
  SECTIONS_WEIGHT_LIMIT
} from '../../models/itinerary-report-sections';
import { ItineraryTemplatesStore } from '../../services/templates.store';

@Component({
  templateUrl: './itinerary-report-drawer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ItineraryTemplatesStore],
  standalone: false
})
export class ItineraryReportDrawerComponent
  extends BaseReportDrawer<CreateItineraryReportDto, GroupedSectionSelectorListItem[]>
  implements OnInit
{
  totalLimitReached: boolean = false;
  alertType = Alert.Info;
  layout: ItineraryReportLayout = ItineraryReportLayout.Standard;
  chipSize = ChipSize.Sm;
  ChipType = ChipType;
  ItineraryReportLayout = ItineraryReportLayout;
  isConfigurableDisabled: boolean = false;
  configurableDisableReason: 'activityLimit' | 'externalParticipantLimit' | undefined;
  isStandardDisabled: boolean = false;
  readonly templatesStore = inject(ItineraryTemplatesStore);

  readonly activityLimit = ITINERARY_MAX_ACTIVITY_SELECTION_LIMIT;
  readonly externalParticipantLimit = ITINERARY_MAX_EXTERNAL_PARTICIPANT_LIMIT;

  isConfigurableToggle = toSignal(this.store.select(getFeatureToggle('configurable-itinerary-export-workflows')));

  setLayout($event: unknown) {
    this.layout = ($event as { id: ItineraryReportLayout }).id;
    if (this.layout === ItineraryReportLayout.Configurable) {
      this.loadConfigurableTemplates();
      this.switchOffSetAsDefault();
    } else {
      this.settingsChange(this.form.controls.sections.value);
      this.drawerService.changeDrawerFooterVisibility(false);
    }
  }

  loadConfigurableTemplates() {
    this.templatesStore.loadTemplates(ReportType.Itinerary);
  }

  ngOnInit() {
    const sections = this.form.controls.sections.value;
    const weightSum = this.calculateWeightSum(sections);
    this.setTotalLimitReached(weightSum);
  }

  override getSections(filteredSections: GroupedSectionSelectorListItem[]): SectionSelectorListItem[] {
    return filteredSections;
  }

  get settingsKey(): string {
    return SETTINGS_KEY;
  }

  get createReportAction() {
    const sections = getSelectedSectionsFromGroups(this.form.controls.sections.value);
    return createItineraryCustomReport({
      eventId: this.request!.eventId,
      eventName: this.request!.eventName,
      sections
    });
  }

  override createReport() {
    if (this.layout === ItineraryReportLayout.Configurable) {
      const activities = this.request?.activities ?? [];

      this.store.dispatch(
        createConfigurableItineraryReport({
          eventId: activities.length === 0 ? this.request!.eventId : '',
          templateId: this.selectedTemplateId!,
          eventName: this.request!.eventName ?? '', // If eventId is empty, eventName will not be used anyway
          activityIds: activities.map(a => a.activityId) ?? []
        })
      );
      this.drawerInstanceRef.setConfirmationModalTrigger(false);
      this.close({ reportInProgress: true });
    } else super.createReport();
  }

  override onInjectInputs(inputs: {
    request?: CreateItineraryReportDto;
    filteredSections: GroupedSectionSelectorListItem[];
    isConfigurableDisabled?: boolean;
    configurableDisableReason?: 'activityLimit' | 'externalParticipantLimit';
  }) {
    super.onInjectInputs(inputs);
    this.isConfigurableDisabled = !!inputs.isConfigurableDisabled;
    this.configurableDisableReason = inputs.configurableDisableReason;
    this.isStandardDisabled = !inputs.request?.eventId;
    if (this.isStandardDisabled && this.isConfigurableToggle() && !this.isConfigurableDisabled) {
      this.setLayout({ id: ItineraryReportLayout.Configurable });
    }
  }

  selectionChange(sections: GroupedSectionSelectorListItem[]) {
    super.settingsChange(sections);
    const weightSum = this.calculateWeightSum(sections);
    this.setTotalLimitReached(weightSum);
    this.form.controls.sections.setValue(this.setToggleDisabled(sections, weightSum), { emitEvent: true });
  }

  private calculateWeightSum(sections: GroupedSectionSelectorListItem[]): number {
    const allSections = sections.flatMap((s: GroupedSectionSelectorListItem) => s.nestedItems || [s]);
    return allSections
      .filter((s: GroupedSectionSelectorListItem) => s.isSelected)
      .reduce((sum: number, s: GroupedSectionSelectorListItem) => sum + (s.weight || 0), 0);
  }

  private isTotalLimitReached(sumWeight: number): boolean {
    return sumWeight === SECTIONS_WEIGHT_LIMIT;
  }

  private setTotalLimitReached(weightSum: number) {
    this.totalLimitReached = this.isTotalLimitReached(weightSum);
  }

  private setToggleDisabled(
    sections: GroupedSectionSelectorListItem[],
    sumWeight: number
  ): GroupedSectionSelectorListItem[] {
    return sections.map((s: GroupedSectionSelectorListItem) => {
      const isToggleDisabled =
        !s.weight || s.isSelected ? s.isToggleDisabled : sumWeight + s.weight > SECTIONS_WEIGHT_LIMIT;

      return {
        ...s,
        isToggleDisabled,
        nestedItems: s.nestedItems ? this.setToggleDisabled(s.nestedItems, sumWeight) : undefined,
        disabledTooltip: this.setDisabledTooltip(s.id, this.totalLimitReached, !!isToggleDisabled)
      };
    });
  }

  private setDisabledTooltip(id: ReportSection, totalLimitReached: boolean, disabled: boolean) {
    if (!disabled) {
      return;
    }
    if (totalLimitReached) {
      return DEFAULT_DISABLED_TOOLTIP;
    }

    return getSectionCustomDisabledTooltip(id);
  }

  get isMobile() {
    return window.innerWidth < Breakpoint.Sm;
  }

  get selectedTemplateId(): number | undefined {
    return this.templatesStore.selectedTemplate()?.templateId;
  }

  previewAndCustomize() {
    if (this.request?.activities?.length) {
      localStorage.setItem(EXPORT_REPORT_ITINERARY_ACTIVITIES, JSON.stringify(this.request.activities));
    } else {
      const event = {
        eventId: this.request?.eventId,
        name: this.request?.eventName,
        startDate: this.request?.startDate,
        endDate: this.request?.endDate,
        type: this.request?.eventType
      };
      localStorage.setItem(EXPORT_REPORT_ITINERARY_EVENT, JSON.stringify(event));
    }
  }

  constructor(store: Store, drawerService: FireflyDrawerService, private localizationService: LocalizationService) {
    super(store, drawerService);
    effect(() => {
      const isLoading = this.templatesStore.loadStatus() === LoadStatus.Loading;
      const noData = !this.templatesStore.templates().length;
      const shouldHide = (isLoading || noData) && this.layout === ItineraryReportLayout.Configurable;
      drawerService.changeDrawerFooterVisibility(shouldHide);
    });
  }
}
